import { useMemo, useState } from 'react'
import { EMOJI_GROUPS, recentEmoji, rememberEmoji, searchUnicode } from '@/lib/emoji'
import { emojiImageUrl } from '@/lib/markdown'
import type { Emoji, ReactionEmoji } from '@/types/api'
import styles from './MediaPicker.module.css'

interface Props {
  /** Кастомные эмодзи: сначала текущего сервера, потом остальных. */
  custom: { guild: string; emojis: Emoji[] }[]
  query: string
  onPick: (emoji: ReactionEmoji) => void
}

/** Содержимое вкладки «Эмодзи»: недавние, кастомные серверов и Unicode по категориям. */
export function EmojiGrid({ custom, query, onPick }: Props) {
  const [recent, setRecent] = useState<string[]>(() => recentEmoji())
  const allCustom = useMemo(() => custom.flatMap((c) => c.emojis), [custom])
  const q = query.trim().toLowerCase()
  const customHits = q ? allCustom.filter((e) => e.name.toLowerCase().includes(q)).slice(0, 24) : []
  const unicodeHits = q ? searchUnicode(q, 48) : []

  const pick = (e: ReactionEmoji) => {
    rememberEmoji(e.id ? `${e.name}:${e.id}` : e.name)
    setRecent(recentEmoji())
    onPick(e)
  }
  const fromToken = (token: string): ReactionEmoji => {
    const m = /^(.+):(\d+)$/.exec(token)
    if (m) {
      const found = allCustom.find((e) => e.id === m[2])
      return { id: m[2] ?? null, name: m[1] ?? '', animated: found?.animated ?? false }
    }
    return { id: null, name: token }
  }

  const renderCustom = (e: Emoji) => (
    <button key={e.id} type="button" className={`${styles.cell} ${styles.emojiCell}`} data-tooltip={`:${e.name}:`} onClick={() => pick({ id: e.id, name: e.name, animated: e.animated })}>
      <img src={e.url ?? emojiImageUrl(e.id, e.animated)} alt={`:${e.name}:`} className={styles.emojiImg} draggable={false} />
    </button>
  )
  const renderUnicode = (ch: string, key: string) => (
    <button key={key} type="button" className={`${styles.cell} ${styles.emojiCell}`} onClick={() => pick({ id: null, name: ch })}>{ch}</button>
  )

  if (q) {
    if (!customHits.length && !unicodeHits.length) return <p className={styles.empty}>Ничего не найдено</p>
    return (
      <>
        {customHits.length ? <div className={styles.group}><div className={styles.groupTitle}>Серверы</div><div className={styles.emojiGrid}>{customHits.map(renderCustom)}</div></div> : null}
        {unicodeHits.length ? <div className={styles.group}><div className={styles.groupTitle}>Эмодзи</div><div className={styles.emojiGrid}>{unicodeHits.map((it) => renderUnicode(it.char, it.char))}</div></div> : null}
      </>
    )
  }

  return (
    <>
      {recent.length ? (
        <div className={styles.group}>
          <div className={styles.groupTitle}>Недавние</div>
          <div className={styles.emojiGrid}>
            {recent.map((token) => {
              const e = fromToken(token)
              return e.id ? (
                <button key={token} type="button" className={`${styles.cell} ${styles.emojiCell}`} data-tooltip={`:${e.name}:`} onClick={() => pick(e)}>
                  <img src={emojiImageUrl(e.id, e.animated)} alt={`:${e.name}:`} className={styles.emojiImg} draggable={false} />
                </button>
              ) : renderUnicode(e.name, token)
            })}
          </div>
        </div>
      ) : null}
      {custom.filter((c) => c.emojis.length).map((c) => (
        <div key={c.guild} className={styles.group}>
          <div className={styles.groupTitle}>{c.guild}</div>
          <div className={styles.emojiGrid}>{c.emojis.map(renderCustom)}</div>
        </div>
      ))}
      {EMOJI_GROUPS.map((g) => (
        <div key={g.id} className={styles.group}>
          <div className={styles.groupTitle}>{g.label}</div>
          <div className={styles.emojiGrid}>{g.items.map((it) => renderUnicode(it.char, it.char))}</div>
        </div>
      ))}
    </>
  )
}
